import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {EventDocumentDataSpeakersItem} from 'prismicio-types'
import {memo} from 'react'
import {Icon} from 'src/common/Icon'

export const SpeakerCard = memo(function ({
  speaker,
  isExpanded,
  onToggle,
  isLast,
  inGridMode = false,
}: {
  speaker: EventDocumentDataSpeakersItem
  isExpanded: boolean
  onToggle: () => void
  isLast: boolean
  inGridMode?: boolean
}) {
  return (
    <div
      className={`flex flex-col gap-4 md:flex-row md:gap-4 ${
        isLast && !inGridMode ? '' : 'border-b border-grey-150 pb-6'
      } ${inGridMode && isLast ? 'md:border-b md:border-grey-150 md:pb-6' : ''}`}
    >
      <div className="flex w-full items-start justify-between md:hidden">
        {isFilled.image(speaker.photo) && (
          <PrismicNextImage
            field={speaker.photo}
            width={80}
            height={80}
            className="size-20 rounded-lg object-cover"
            quality={90}
          />
        )}
        {isFilled.link(speaker.linkedin_link) && (
          <a
            href={speaker.linkedin_link.url || ''}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-b8b6b4 transition-colors hover:text-2a2928"
            aria-label={`${speaker.name}'s LinkedIn profile`}
          >
            <Icon name="linkedin-logo" className="size-6 text-grey-300 group-hover:brightness-[1.15]" />
          </a>
        )}
      </div>

      {isFilled.image(speaker.photo) && (
        <div className="hidden shrink-0 md:block">
          <PrismicNextImage
            field={speaker.photo}
            width={80}
            height={80}
            className="size-20 rounded-lg object-cover"
            quality={90}
          />
        </div>
      )}

      <div className="flex-1">
        <div className="mb-1 flex items-start justify-between">
          <div className="flex-1">
            {isFilled.keyText(speaker.name) && (
              <h3 className="mb-1 text-18 font-medium leading-[24px] text-moss-black md:mb-1">{speaker.name}</h3>
            )}

            <div className="flex flex-col gap-2 md:hidden">
              {isFilled.keyText(speaker.title) && (
                <p className="text-12 font-semibold uppercase tracking-wide text-moss-grey-400">{speaker.title}</p>
              )}
              {isFilled.keyText(speaker.company) && (
                <p className="text-12 font-semibold uppercase tracking-wide text-moss-grey-400">{speaker.company}</p>
              )}
            </div>

            {(isFilled.keyText(speaker.title) || isFilled.keyText(speaker.company)) && (
              <p className="hidden items-center gap-2 text-12 font-semibold uppercase tracking-wide text-moss-grey-400 md:flex lg:text-10">
                {isFilled.keyText(speaker.title) && <span>{speaker.title}</span>}
                {isFilled.keyText(speaker.title) && isFilled.keyText(speaker.company) && (
                  <svg width="4" height="4" viewBox="0 0 4 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="4" height="4" rx="1" fill="#CCCCCC" />
                  </svg>
                )}
                {isFilled.keyText(speaker.company) && <span>{speaker.company}</span>}
              </p>
            )}
          </div>

          {isFilled.link(speaker.linkedin_link) && (
            <a
              href={speaker.linkedin_link.url || ''}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden shrink-0 text-grey-200 transition-colors hover:text-2a2928 md:block"
              aria-label={`${speaker.name}'s LinkedIn profile`}
            >
              <Icon name="linkedin-logo" className="size-6 text-grey-300 group-hover:brightness-[1.15]" />
            </a>
          )}
        </div>

        {isFilled.keyText(speaker.speaker_bio) && (
          <div className="mt-3">
            <button
              onClick={onToggle}
              className={`${isExpanded ? 'mb-2' : ''} flex items-center gap-2 text-15 font-medium text-moss-black`}
              aria-expanded={isExpanded}
            >
              {isExpanded ? 'Collapse Bio' : 'Read Bio'}
              <Icon
                name="chevron"
                className={`size-4 transition-transform duration-500 ease-custom-ease ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>
            <div
              className={`grid transition-[grid-template-rows,opacity] duration-500 ease-custom-ease ${
                isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
              }`}
            >
              <div className="overflow-hidden">
                <p className="text-14 leading-5 text-grey-550">{speaker.speaker_bio}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
})
